import { useEffect, useRef, useState } from "react";
import {
  appVersionCheckIntervalMs,
  fetchPublishedMainBundlePath,
  getRunningMainBundlePath,
  isNewAppVersionPublished,
} from "../utils/appVersionCheck";

type UseNewAppVersionAvailableInput = {
  fetchPublishedPath?: () => Promise<string | null>;
  intervalMs?: number;
  runningMainBundlePath?: string | null;
};

/**
 * True once the published dashboard differs from the code this tab runs.
 * Checks every few minutes and when the tab gets focus again (Ajax Oct 7
 * 2026: a tab opened on Oct 5 still ran the Oct 5 release). It never
 * reloads the page; staff choose when to reload.
 */
const useNewAppVersionAvailable = ({
  fetchPublishedPath = fetchPublishedMainBundlePath,
  intervalMs = appVersionCheckIntervalMs,
  runningMainBundlePath,
}: UseNewAppVersionAvailableInput = {}): boolean => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const runningPathRef = useRef<string | null>(
    runningMainBundlePath === undefined
      ? getRunningMainBundlePath()
      : runningMainBundlePath,
  );
  const checkInFlightRef = useRef(false);

  useEffect(() => {
    if (!runningPathRef.current || newVersionAvailable) {
      return undefined;
    }

    let active = true;
    const check = async () => {
      if (checkInFlightRef.current) {
        return;
      }

      checkInFlightRef.current = true;
      try {
        const publishedMainBundlePath = await fetchPublishedPath();
        if (
          active &&
          isNewAppVersionPublished({
            publishedMainBundlePath,
            runningMainBundlePath: runningPathRef.current,
          })
        ) {
          setNewVersionAvailable(true);
        }
      } finally {
        checkInFlightRef.current = false;
      }
    };
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void check();
      }
    };
    const intervalId = window.setInterval(checkWhenVisible, intervalMs);

    window.addEventListener("focus", checkWhenVisible);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkWhenVisible);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [fetchPublishedPath, intervalMs, newVersionAvailable]);

  return newVersionAvailable;
};

export default useNewAppVersionAvailable;
